import React from "react";
import { Document, Font, Page, StyleSheet, View } from "@react-pdf/renderer";
import Text from "./HandoverPdfText";
import { companyLabel, signatureCompanyLabel, remainingPercentage, thaiDate, validPercentage } from "../utils/handover";

Font.register({
  family: "NotoSansThaiHandover",
  fonts: [
    { src: "/fonts/NotoSansThai-Regular.ttf" },
    { src: "/fonts/NotoSansThai-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  // These measurements mirror handover.css: CSS pixels are converted to PDF points (px × 0.75).
  page: { paddingTop: 62, paddingBottom: 62, paddingHorizontal: 45, fontFamily: "NotoSansThaiHandover", fontSize: 10.5, color: "#111827", lineHeight: 1.95 },
  title: { fontSize: 15, fontWeight: "bold", textAlign: "center", marginBottom: 18 },
  date: { textAlign: "right", marginBottom: 33 },
  paragraph: { textIndent: 22.5, marginBottom: 9, textAlign: "left" },
  bottom: { flexDirection: "row", gap: 18, marginTop: 33, alignItems: "flex-start" },
  remark: { flexGrow: 1, flexBasis: 0 },
  remarkTitle: { fontWeight: "bold" },
  signatures: { flexGrow: 1, flexBasis: 0 },
  signature: { textAlign: "center", fontSize: 10.5, lineHeight: 1.45, marginTop: 14 },
  signatureRole: { marginTop: 12, marginBottom: 18 },
});

const safePerson = (person = {}) => ({ name: person.name || "ชื่อผู้ลงนาม", position: person.position || "", clientId: person.clientId || "" });

export default function HandoverPdfDocument({ document, values, clients, quotationClientId }) {
  const label = (id, snapshot) => companyLabel(id, snapshot, clients);
  const quotationClient = quotationClientId || values.deliveredToClientId;
  const remaining = String(document.completionPercentage) === values.completionPercentage
    ? document.remainingPercentage
    : remainingPercentage(values.completionPercentage);
  const completionText = validPercentage(values.completionPercentage) && Number(values.completionPercentage) === 100
    ? "บัดนี้ได้ดำเนินงานดังกล่าวเสร็จสิ้นแล้ว 100%"
    : `บัดนี้ได้ดำเนินงานเสร็จสิ้นแล้ว ${validPercentage(values.completionPercentage) ? Number(values.completionPercentage) : "…"}% อีก ${remaining ?? "…"}% จะเสร็จสิ้นในเดือน${thaiDate(values.expectedCompletionMonth, true)}`;
  const people = [
    { person: safePerson(values.sender), snapshot: document.sender, role: "ผู้ส่งมอบงาน", isRecipient: false },
    ...values.recipients.slice(0, values.additionalRecipientEnabled ? 2 : 1).map((person, index) => ({ person: safePerson(person), snapshot: document.recipients?.[index], role: "ผู้รับมอบงาน", isRecipient: true })),
  ];

  return (
    <Document title={`หนังสือส่งมอบงาน ${document.quotationNumber || ""}`} author={document.issuerCompany?.name || ""}>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>หนังสือส่งมอบงาน</Text>
        <Text style={styles.date}>วันที่ {thaiDate(values.documentDate)}</Text>
        <Text style={styles.paragraph}>
          ตามที่ข้าพเจ้า {document.issuerCompany?.name} ได้รับการว่าจ้างจาก {label(quotationClient, document.deliveredToClient)} ให้ทำการดำเนินงานแคมเปญ  {document.projectName} ประเภทงาน {document.workType} ตามเอกสารใบสั่งซื้อเลขที่ {document.quotationNumber} นั้น {completionText} โดยได้ส่งมอบเอกสารและสิ่งอื่นๆ ที่เกี่ยวข้องกับการดำเนินงาน (ถ้ามี) ให้กับ {label(values.deliveredToClientId, document.deliveredToClient)} เรียบร้อยแล้ว
        </Text>
        <Text style={styles.paragraph}>จึงเรียนมาเพื่อโปรดพิจารณาอนุมัติ</Text>
        <View style={styles.bottom}>
          <View style={styles.remark}>
            {values.remarkEnabled && <><Text style={styles.remarkTitle}>หมายเหตุ</Text><Text>{values.remark}</Text></>}
          </View>
          <View style={styles.signatures}>
            {people.map(({ person, snapshot, role, isRecipient }, index) => (
              <View key={`${role}-${index}`} style={styles.signature} wrap={false}>
                <Text>ลงชื่อ ................................................</Text>
                <Text>({person.name})</Text>
                {(!isRecipient || person.position) && <Text>{person.position || "ตำแหน่ง\u00a0"}</Text>}
                {(!isRecipient || person.clientId) && <Text>{signatureCompanyLabel(person.clientId, snapshot?.company, clients)}</Text>}
                <Text style={styles.signatureRole}>{role}</Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
}
